import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Modal } from "ui-kit/Modal";
import { Button } from "ui-kit/Button";
import { Checkbox } from "ui-kit/PrimitiveControls/Checkbox";
import { Select } from "ui-kit/PrimitiveControls/Select";
import { Input } from "ui-kit/PrimitiveControls/Input";
import { Artist } from "types/artist";
import { PlatformLinks } from "../../hooks/useArtists";
import { useForm } from "react-final-form";
import { FormValues } from "components/NewRelease/ReleaseInfo/types";
import { FormattedMessage } from "react-intl";
import IntlStore from "stories/IntlStore";
import Copy from '../../../../assets/copy.svg';
import { useTracks } from 'components/NewRelease/hooks/useTracks';
import { useNavigate, useParams } from 'react-router';

interface AddArtistModalProps {
  isOpen: boolean;
  artistCreating: boolean;
  onHide: () => void;
  artists: Artist[];
  createArtist: (name: string, links: PlatformLinks[], status: boolean,) => Promise<Artist>;
  selectedArtist: string;
  setSelectedArtist: (selectedArtist: string) => void
  trackId:any
}

const SpotifyId = '1';
const AppleMusicId = '2';

export const AddArtistModal: React.FC<AddArtistModalProps> = props => {
  const { isOpen, onHide, artists, createArtist, artistCreating,trackId } = props;
  const [hasSpotify, setHasSpotify] = useState<boolean | null>(null);
  const [spotifyUrl, setSpotifyUrl] = useState('');
  const [hasAppleMusic, setHasAppleMusic] = useState<boolean | null>(null);
  const [appleUrl, setAppleUrl] = useState('');
  const [value, setValue] = useState('')
  const { change, getState } = useForm<FormValues>();
  const [artistData, setArtistData] = useState<Artist[]>([])
  const {updateTrack} = useTracks()
  const values = getState().values;
  
  const resetModalState = () => {
    setHasSpotify(null);
    setHasAppleMusic(null);
    setSpotifyUrl('');
    setAppleUrl('');
    setValue('');
    props.setSelectedArtist("");
  };

  const validateSpotify = (value: string) => {
    const regex = /^(https:\/\/open\.spotify\.com\/artist\/[A-Za-z0-9]+|spotify:artist:[A-Za-z0-9]+)$/;
    return regex.test(value);
  };

  const validateApple = (value: string) => {
    const regex = /^(https:\/\/music\.apple\.com(\/[a-z]{2})?\/artist\/[a-z0-9\-]+\/\d+$|\d+)$/i;
    return regex.test(value);
  };

  const isSaveDisabled =
    props.selectedArtist?.length === 0 ||
    hasSpotify === null ||
    hasAppleMusic === null ||
    (hasSpotify && (spotifyUrl?.length === 0 || !validateSpotify(spotifyUrl))) ||
    (hasAppleMusic && (appleUrl?.length === 0 || !validateApple(appleUrl)));

    const onCreateArtist = async () => {
      try {
        const platformLinks = [];
    
        if (hasSpotify) {
          platformLinks.push({
            social_platform_id: SpotifyId,
            url: spotifyUrl,
            status: false
          });
        }
    
        if (hasAppleMusic) {
          platformLinks.push({
            social_platform_id: AppleMusicId,
            url: appleUrl,
            status: false
          });
        }
    
        let artist = artistData?.find((a) => a.id == props.selectedArtist);
        if (!artist) {
          artist = await createArtist(value, platformLinks, false);
        }
    
        // ---- EXISTING ADDED ARTISTS (from form) ----
        const currentArtists = Array.isArray(values.artists) ? values.artists : [];
    
        const hasMain = currentArtists.some(a => a.role === "MAIN");
    
        // --- ASSIGN ROLE ---
        const newArtistRole = hasMain ? "FEAT" : "MAIN";
    
        const updatedList = [
          ...currentArtists.map(a => ({
            ...a,
            role: a.role === "MAIN" && newArtistRole === "MAIN" ? "FEAT" : a.role
          })),
          {
            id: artist.id,
            title: artist.name,
            status: artist.status || true,
            role: newArtistRole
          }
        ];
    
        change("artists", updatedList);
        // Reset immediately so quick reopen doesn't keep previous selection
        resetModalState();
        onHide();
        await updateTrack(
          props.trackId,
          {
            artist_ids: updatedList.map(a => a.id)
          } as any
        );
    
      
        // (state already reset above)
        
    
      } catch (error) {
        console.error("Error creating artist:", error);
      }
    };
    

  useEffect(() => {
    const artistData = artists
    setValue(
      !isNaN(Number(props.selectedArtist))
        ? artistData?.find((val) => val.id == props.selectedArtist)?.name || ''
        : props.selectedArtist)
  }, [props.selectedArtist])

  useEffect(() => {
    if (artists?.length) {
      setArtistData(artists)
    }
  }, [artists])

  return (
    <Modal
      visible={isOpen}
      onHide={() => {
        resetModalState();
        onHide();
      }}
      dismissableMask
      showHeader={false}
      header={null}
      footer={(
        <Footer>
          <Button loading={artistCreating} disabled={isSaveDisabled} onClick={onCreateArtist}>
            <FormattedMessage
              id="NewRelease.ReleaseInfo.AddArtist"
              defaultMessage="Add artist"
            />
          </Button>
        </Footer>
      )}
    >
      <FormContainer>
        <Section>
          <Select
            editable={value ? true : false}
            label={IntlStore.intl.formatMessage({
              id: "NewRelease.ReleaseInfo.Artist",
              defaultMessage: "Artist"
            })}
            name="artist"
            value={
              value
            }
            items={artists
              .filter(a =>
                !Array.isArray(values.artists)
                  ? true
                  : !values.artists.some(
                      selected => String((selected as any)?.id) === String(a.id)
                    )
              )
              .map(artist => ({ label: artist.name, value: artist.id }))}
            onChange={(e) => {
              props.setSelectedArtist(e.value);
              setValue(e.value)
              const artist = artists.find((artist) => artist.id === e.value);
              // if (artist) {
              //   setHasSpotify(false);
              //   setHasAppleMusic(false);
              //   artist.platforms.forEach(platform => {
              //     if (platform.id == SpotifyId) {
              //       setHasSpotify(true);
              //       setSpotifyUrl(platform.url || '');
              //     }
              //     if (platform.id == AppleMusicId) {
              //       setHasAppleMusic(true);
              //       setAppleUrl(platform.url || '');
              //     }
              //   });
              // }
            }}
            placeholder={IntlStore.intl.formatMessage({
              id: "NewRelease.ReleaseInfo.ArtistPlaceholder",
              defaultMessage: "Select artist"
            })}
            addAsNew={true}
          />
        </Section>
        <Section>
          <Label>
            <FormattedMessage
              id="NewRelease.ReleaseInfo.Question1"
              defaultMessage="Does this artist have music live on Spotify?"
            />
          </Label>
          <CheckboxGroup>
            <div>
              <Checkbox
                name="spotifyYes"
                label={IntlStore.intl.formatMessage({
                  id: "NewRelease.ReleaseInfo.Yes",
                  defaultMessage: "Yes"
                })}
                checked={hasSpotify === true}
                onChange={() => setHasSpotify(true)}
              />
            </div>
            <div>
              <Checkbox
                name="spotifyNo"
                label={IntlStore.intl.formatMessage({
                  id: "NewRelease.ReleaseInfo.No",
                  defaultMessage: "No"
                })}
                checked={hasSpotify === false}
                onChange={() => setHasSpotify(false)}
              />
            </div>
          </CheckboxGroup>
          {hasSpotify ? (
            <InputBox>
              <Input
                name="spotifyUrl"
                value={spotifyUrl}
                onChange={(e) => setSpotifyUrl(e.target.value)}
                placeholder={IntlStore.intl.formatMessage({
                  id: "NewRelease.ReleaseInfo.SpotifyUrl",
                  defaultMessage: "Paste the full URL to your Spotify artist profile"
                })}
              />
              <CopyBox onClick={() => {
                navigator.clipboard.writeText(spotifyUrl);
              }}>
                <Copy />
              </CopyBox>
            </InputBox>
          ) : hasSpotify === false && (
            <Warning>
              <FormattedMessage
                id="NewRelease.ReleaseInfo.Warning1"
                defaultMessage="We will create a new profile for this artist on Spotify"
              />
            </Warning>
          )}
        </Section>
        <Section>
          <Label>
            <FormattedMessage
              id="NewRelease.ReleaseInfo.Question2"
              defaultMessage="Does this artist have music live on Apple Music?"
            />
          </Label>
          <CheckboxGroup>
            <div>
              <Checkbox
                name="appleYes"
                label={IntlStore.intl.formatMessage({
                  id: "NewRelease.ReleaseInfo.Yes",
                  defaultMessage: "Yes"
                })}
                checked={hasAppleMusic === true}
                onChange={() => setHasAppleMusic(true)}
              />
            </div>
            <div>
              <Checkbox
                name="appleNo"
                label={IntlStore.intl.formatMessage({
                  id: "NewRelease.ReleaseInfo.No",
                  defaultMessage: "No"
                })}
                checked={hasAppleMusic === false}
                onChange={() => setHasAppleMusic(false)}
              />
            </div>
          </CheckboxGroup>
          {hasAppleMusic ? (
            <InputBox>
              <Input
                name="appleUrl"
                value={appleUrl}
                onChange={(e) => setAppleUrl(e.target.value)}
                placeholder={IntlStore.intl.formatMessage({
                  id: "NewRelease.ReleaseInfo.AppleUrlPlaceholder",
                  defaultMessage: "Paste the full URL to your Apple Music artist page"
                })}
              />
              <CopyBox onClick={() => {
                navigator.clipboard.writeText(spotifyUrl);
              }}>
                <Copy />
              </CopyBox>
            </InputBox>
          ) : hasAppleMusic === false && (
            <Warning>
              <FormattedMessage
                id="NewRelease.ReleaseInfo.Warning2"
                defaultMessage="We will create a new profile for this artist on Apple Music"
              />
            </Warning>
          )}
        </Section>
      </FormContainer>
    </Modal>
  );
};

const FormContainer = styled.div`
  width: 490px;
  padding: 16px 0 0;

  /* Mobile: fit dialog into viewport and allow scrolling within content */
  @media (max-width: 768px) {
    width: min(100%);
    max-height: calc(100dvh - 180px);
    overflow: auto;
    -webkit-overflow-scrolling: touch;

    /* Prevent iOS from zooming when focusing inputs in this modal */
    input,
    select,
    textarea {
      font-size: 16px;
    }
  }
`;
const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  margin-bottom: 20px;

  
  &:last-child {
    margin-bottom: 0;
  }
`
const CheckboxGroup = styled.div`
  display: flex;
  gap: 30px;
  align-items: center;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 12px 20px;
  }
`
const Label = styled.div`
  font-weight: 500;
  font-size: 13px;
  line-height: 18px;
`
const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid #00000033;
  padding: 12px 16px 0 24px;
  margin: 0 -24px;

  /* Mobile: avoid negative margins causing horizontal overflow */
  @media (max-width: 768px) {
    margin: 0;
    padding: 12px 0 0;
  }
`
const Warning = styled.div`
  border-radius: 15px;
  padding: 18px 16px;
  background-color: #EFFFF1;
  font-weight: 500;
  font-size: 13px;
  line-height: 18px;
`
const InputBox = styled.div`
  display: flex;
  align-items: center;
  position: relative;

  & > input {
    flex-grow: 1;
  }
`;

const CopyBox = styled.div`
  position: absolute;
  right: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background-color: #F0F4FF;
  border-radius: 50%;
`